/**
 * Wallet API Client
 *
 * Fetch wallet balance, create top-ups, and estimate job costs.
 * Uses shared apiCall helper for authenticated requests.
 */

import { apiCall } from './api-helpers';

export interface WalletBalanceResponse {
  balance: string;
  lowBalance?: boolean;
}

export interface TopUpResponse {
  clientSecret: string;
  paymentIntentId: string;
}

export interface JobEstimateResponse {
  priceUsd: string;
  provider: string;
  model: string;
}

/**
 * Settings that change what a run costs, as GET /api/jobs/estimate accepts them
 * (apps/api-app/src/routes/jobs-estimate.ts, EstimateContextSchema). Each entry is the upper
 * bound the route validates against, so an out-of-range value is left out rather than turning
 * the whole quote into a 400.
 */
const ESTIMATE_NUMBER_SETTINGS: Record<string, { max: number; int?: boolean }> = {
  duration: { max: 3600 },
  duration_seconds: { max: 3600 },
  audio_duration_seconds: { max: 3600 },
  seconds_total: { max: 3600 },
  num_frames: { max: 100_000, int: true },
  number_of_frames: { max: 100_000, int: true },
  frames: { max: 100_000, int: true },
  fps: { max: 240 },
  frames_per_second: { max: 240 },
  frame_rate: { max: 240 },
  width: { max: 16_384, int: true },
  height: { max: 16_384, int: true },
  max_tokens: { max: 10_000_000, int: true },
  max_new_tokens: { max: 10_000_000, int: true },
};

const ESTIMATE_STRING_SETTINGS: Record<string, number> = {
  image_size: 64,
  video_size: 64,
  resolution: 64,
  aspect_ratio: 32,
};

/**
 * Reduce a node's settings to the query parameters the estimate route prices from.
 * Everything else (prompt, seed, LoRAs, ...) is dropped, which keeps the request small and the
 * estimate cache keyed only on what moves the price.
 */
export function pickEstimateSettings(
  settings: Record<string, unknown> | null | undefined,
): Record<string, string> {
  const picked: Record<string, string> = {};
  if (!settings) return picked;

  const addNumber = (key: string, raw: unknown) => {
    const rule = ESTIMATE_NUMBER_SETTINGS[key];
    const value = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
    if (!rule || typeof value !== 'number' || !Number.isFinite(value)) return;
    if (value <= 0 || value > rule.max || (rule.int && !Number.isInteger(value))) return;
    picked[key] = String(value);
  };

  for (const key of Object.keys(ESTIMATE_NUMBER_SETTINGS)) addNumber(key, settings[key]);

  for (const [key, maxLength] of Object.entries(ESTIMATE_STRING_SETTINGS)) {
    const value = settings[key];
    if (typeof value === 'string' && value.length > 0 && value.length <= maxLength) {
      picked[key] = value;
    } else if (value && typeof value === 'object') {
      // fal-style custom sizes are { width, height } objects rather than a preset name.
      const size = value as { width?: unknown; height?: unknown };
      if (!('width' in picked)) addNumber('width', size.width);
      if (!('height' in picked)) addNumber('height', size.height);
    }
  }

  return picked;
}

export interface PaymentMethodResponse {
  id: string;
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

export interface SetupIntentResponse {
  clientSecret: string;
}

import type { StudioWalletTransaction as WalletTransaction } from '@genieartinc/schemas/graph'
export type { StudioWalletTransaction as WalletTransaction } from '@genieartinc/schemas/graph'

export interface WalletTransactionsResponse {
  transactions: WalletTransaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amount: number;
  currency: string;
  created: number;
  periodStart: number | null;
  periodEnd: number | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  description: string | null;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  hasMore: boolean;
}

export const walletAPI = {
  /**
   * Get the current user's wallet balance.
   */
  async getBalance(isAuthenticated: boolean): Promise<WalletBalanceResponse> {
    const result = await apiCall('/api/wallet/balance', isAuthenticated) as {
      success: boolean;
      data: WalletBalanceResponse;
    };
    return result.data;
  },

  /**
   * Create a top-up payment intent.
   */
  async createTopUp(amount: number, isAuthenticated: boolean): Promise<TopUpResponse> {
    const result = await apiCall('/api/wallet/topup', isAuthenticated, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }) as { success: boolean; data: TopUpResponse };
    return result.data;
  },

  /**
   * Get the current user's wallet transaction history.
   */
  async getTransactions(
    isAuthenticated: boolean,
    options?: { type?: string; canvasId?: string; limit?: number; offset?: number },
  ): Promise<WalletTransactionsResponse> {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.canvasId) params.set('canvasId', options.canvasId);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    const url = `/api/wallet/transactions${qs ? `?${qs}` : ''}`;
    const result = await apiCall(url, isAuthenticated) as {
      success: boolean;
      data: WalletTransactionsResponse;
    };
    return result.data;
  },

  /**
   * List saved payment methods for the current user.
   */
  async getPaymentMethods(isAuthenticated: boolean): Promise<PaymentMethodResponse[]> {
    const result = await apiCall('/api/wallet/payment-methods', isAuthenticated) as {
      success: boolean;
      data: { paymentMethods: PaymentMethodResponse[] };
    };
    return result.data.paymentMethods;
  },

  /**
   * Create a Stripe SetupIntent for adding a new payment method.
   */
  async createSetupIntent(isAuthenticated: boolean): Promise<SetupIntentResponse> {
    const result = await apiCall('/api/wallet/payment-methods/setup', isAuthenticated, {
      method: 'POST',
    }) as { success: boolean; data: SetupIntentResponse };
    return result.data;
  },

  /**
   * Remove a saved payment method.
   */
  async removePaymentMethod(paymentMethodId: string, isAuthenticated: boolean): Promise<void> {
    await apiCall(`/api/wallet/payment-methods/${paymentMethodId}`, isAuthenticated, {
      method: 'DELETE',
    });
  },

  /**
   * Set a payment method as the default.
   */
  async setDefaultPaymentMethod(paymentMethodId: string, isAuthenticated: boolean): Promise<void> {
    await apiCall(`/api/wallet/payment-methods/${paymentMethodId}/default`, isAuthenticated, {
      method: 'PUT',
    });
  },

  /**
   * Get the current user's Stripe invoice history.
   */
  async getInvoices(
    isAuthenticated: boolean,
    options?: { limit?: number; startingAfter?: string },
  ): Promise<InvoicesResponse> {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.startingAfter) params.set('starting_after', options.startingAfter);
    const qs = params.toString();
    const url = `/api/wallet/invoices${qs ? `?${qs}` : ''}`;
    const result = await apiCall(url, isAuthenticated) as {
      success: boolean;
      data: InvoicesResponse;
    };
    return result.data;
  },

  /**
   * Estimate the cost of a job before running it. Pass the node's settings so the quote is
   * priced from its duration, size and token budget rather than the route's defaults.
   */
  async getJobEstimate(
    provider: string,
    model: string,
    isAuthenticated: boolean,
    settings?: Record<string, unknown> | null,
  ): Promise<JobEstimateResponse> {
    const params = new URLSearchParams({ provider, model, ...pickEstimateSettings(settings) });
    const result = await apiCall(
      `/api/jobs/estimate?${params}`,
      isAuthenticated,
    ) as { success: boolean; data: JobEstimateResponse };
    return result.data;
  },
};
